import moment from 'moment';

// Utility functions for session time management
export const isSessionJoinable = (session) => {
    if (!session || !session.date || !session.startTime || !session.endTime) {
        return false;
    }

    // CRITICAL: Check if referral is verified by the session doctor
    // If canStartSession is false, session cannot be joined regardless of time
    if (session.canStartSession === false) {
        console.log('Session not joinable: Referral not verified by session doctor');
        return false;
    }

    const now = moment();
    const sessionDate = moment(session.date);
    const sessionStart = moment(`${session.date} ${session.startTime}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm A"]);
    const sessionEnd = moment(`${session.date} ${session.endTime}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm A"]);

    // Session must be today or in the future
    if (sessionDate.isBefore(now, 'day')) {
        return false;
    }

    // If session is today, check if we're within the join window
    if (sessionDate.isSame(now, 'day')) {
        const diffToStart = sessionStart.diff(now, 'minutes');
        // Enable 5 minutes before start time
        const canJoinEarly = diffToStart <= 5 && diffToStart >= -1 * sessionEnd.diff(sessionStart, 'minutes');
        // Debug log
        console.log('isSessionJoinable:', {
            now: now.format(),
            sessionStart: sessionStart.format(),
            diffToStart,
            canJoinEarly,
            sessionDate: sessionDate.format(),
            sessionEnd: sessionEnd.format(),
            canStartSession: session.canStartSession,
            session
        });
        // Enable during the session
        const isDuringSession = now.isAfter(sessionStart) && now.isBefore(sessionEnd);
        return canJoinEarly || isDuringSession;
    }

    return false;
};

export const getSessionStatus = (session) => {
    if (!session || !session.date || !session.startTime) {
        return 'unknown';
    }

    const now = moment();
    const sessionDate = moment(session.date);
    const sessionStart = moment(`${session.date} ${session.startTime}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm A"]);
    const sessionEnd = moment(`${session.date} ${session.endTime}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm A"]);

    // If session is completed
    if (session.status === 'completed') {
        return 'completed';
    }

    // If session is cancelled
    if (session.status === 'cancelled') {
        return 'cancelled';
    }

    // If session is in progress
    if (session.status === 'in_progress') {
        return 'in_progress';
    }

    // If session is in the past
    if (sessionDate.isBefore(now, 'day') || (sessionDate.isSame(now, 'day') && sessionEnd.isBefore(now))) {
        return 'missed';
    }

    // If session is today and within join window
    if (sessionDate.isSame(now, 'day') && isSessionJoinable(session)) {
        return 'joinable';
    }

    // If session is in the future
    if (sessionDate.isAfter(now, 'day') || (sessionDate.isSame(now, 'day') && sessionStart.isAfter(now))) {
        return 'scheduled';
    }

    return 'unknown';
};

export const getTimeUntilSession = (session) => {
    if (!session || !session.date || !session.startTime) {
        return null;
    }

    const now = moment();
    const sessionStart = moment(`${session.date} ${session.startTime}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm A"]);

    // Use Math.ceil to avoid off-by-one minute issue
    const diffMs = sessionStart.diff(now);
    const diffMinutes = Math.ceil(diffMs / 60000);

    if (diffMinutes > 0) {
        const days = Math.floor(diffMinutes / (60 * 24));
        const hours = Math.floor((diffMinutes % (60 * 24)) / 60);
        const minutes = diffMinutes % 60;

        let result = '';
        if (days > 0) result += `${days}d `;
        if (hours > 0) result += `${hours}h `;
        if (minutes > 0 || (!days && !hours)) result += `${minutes}m`;
        return result.trim();
    }

    return null;
};

export const shouldShowJoinButton = (session) => {
    if (!session) return false;

    const status = getSessionStatus(session);

    // Show join button for scheduled sessions that are joinable
    return status === 'joinable' || status === 'in_progress';
};

export const isSessionCompleted = (session) => {
    return session?.status === 'completed';
};

export const isSessionInProgress = (session) => {
    return session?.status === 'in_progress';
};

export const isSessionScheduled = (session) => {
    return session?.status === 'scheduled';
};


